// 批量额度(单次批量文件数上限),与后端 sharkfoto-fastapi src/index.ts 的 BATCH_LIMITS 保持一致。
// 前端用于 UX(锁超额文件 + 弹注册/付费墙);真正的强制在后端 /batch（前端绕不过)。
// anon=未登录 / free=登录免费 / paid=付费。<1 = 该档禁用(需升级)。
export const BATCH_LIMITS = {
  image: { anon: 1, free: 5, paid: 30 }, // HEIC/AVIF/SVG/CAD/ICNS 等需服务端的轻图像 + 纯前端常见图像
  raw: { anon: 1, free: 5, paid: 20 },
  audio: { anon: 1, free: 5, paid: 15 },
  video: { anon: 1, free: 1, paid: 5 },
  office: { anon: 1, free: 1, paid: 10 },
  embroidery: { anon: 0, free: 0, paid: 10 }, // 纯付费
};

// 纯前端类(零服务器成本):flip/rotate/resize/水印/压缩常见格式等 —— 额度给得大方,纯防浏览器崩。
// 这类没有服务端成本,所以未登录也给一个体验额度,但仍引导注册(留资)。
export const CLIENT_BATCH_LIMITS = { anon: 1, free: 20, paid: 100 };

// 从套餐信息判档:product_name 非 'Free' = paid;有 token/登录但 Free = free;否则 anon。
export function tierOf(loggedIn, userInfo) {
  if (!loggedIn) return "anon";
  const pn = userInfo && userInfo.product_name;
  return pn && pn !== "Free" ? "paid" : "free";
}

// 取某类目某档的上限。client=纯前端类(用 CLIENT_BATCH_LIMITS)。
export function batchLimit(category, tier, client) {
  if (client) return CLIENT_BATCH_LIMITS[tier] ?? 1;
  const row = BATCH_LIMITS[category] || BATCH_LIMITS.image;
  return row[tier] ?? 1;
}

// 每文件大小上限(字节)。产品限额,比后端容器 MAX_BYTES(200MB)更严:
// 大文件转换慢 + 独占稀缺容器(媒体仅 2 实例)+ 产出没实用价值 → 前端即时拦截、给清晰提示(AE 审计 ③,Jack 定)。
export const PER_FILE_MAX = {
  image: 50 * 1024 * 1024,   // 客户端 canvas + 轻服务端图像
  raw: 80 * 1024 * 1024,     // 相机 RAW 本就大(高像素 30–80MB)、走 core 容器不占媒体槽 → 保持较高
  office: 30 * 1024 * 1024,
  audio: 50 * 1024 * 1024,
  video: 50 * 1024 * 1024,   // 媒体容器仅 2 实例 + 转码慢:>50MB 必超时
  embroidery: 25 * 1024 * 1024,
};
export function perFileMax(category) {
  return PER_FILE_MAX[category] || PER_FILE_MAX.image;
}

// 单文件超限提示:超限返回文案(供各工具用自己的错误 UI 展示 alert/toast/ref),未超返回 ""。
// 客户端 canvas 图像工具尤其需要:超大图(上亿像素)进 canvas 会吃爆内存、崩标签页。
export function fileTooLargeMsg(file, category) {
  const max = perFileMax(category);
  if (file && file.size > max) {
    return `File is too large (${(file.size / 1048576).toFixed(1)} MB). Max is ${Math.round(max / 1048576)} MB for this tool.`;
  }
  return "";
}

// 浏览器 canvas 能直接解码的图像格式。不在此列的(heic/tiff/tga/dds/psd…)客户端编辑工具解不了,
// 统一先经后端转成 png 再进 canvas(用户无感)。见 useCanvasDecode。
export const BROWSER_DECODABLE = { png: 1, jpg: 1, jpeg: 1, jfif: 1, webp: 1, gif: 1, bmp: 1, svg: 1, ico: 1, avif: 1 };
export function isBrowserDecodable(ext) {
  return !!BROWSER_DECODABLE[String(ext || "").toLowerCase().replace(/^\./, "")];
}

// 编辑工具(resize/crop/…)可接收的图像扩展名:浏览器可解的 + 后端能解码成 png 的常见栅格
// (heic/tiff/tga/dds/psd/jp2/netpbm…)。据此放行入口 —— 很多这类文件 MIME 为空,只按 image/* 会误拒。
// 真解不了的(后端也读不了)进 decodeForCanvas 时才失败并给提示。相机 RAW 归 converter,不在编辑入口。
export const EDITABLE_IMAGE_EXTS = {
  png: 1, jpg: 1, jpeg: 1, jfif: 1, webp: 1, gif: 1, bmp: 1, svg: 1, ico: 1, avif: 1, dib: 1,
  heic: 1, heif: 1, hif: 1, heics: 1, tif: 1, tiff: 1, tga: 1, dds: 1, psd: 1, psb: 1,
  jp2: 1, j2k: 1, jpx: 1, jpf: 1, pcx: 1, ppm: 1, pgm: 1, pbm: 1, pnm: 1, xbm: 1, xpm: 1, sgi: 1, ras: 1,
};
// 入口判定:有 image/* MIME,或扩展名在可编辑图像集里(覆盖 MIME 为空的 dds/psd 等)。
export function looksLikeEditableImage(file) {
  if (!file) return false;
  if (String(file.type || "").startsWith("image/")) return true;
  const ext = (String(file.name || "").match(/\.([a-z0-9]+)$/i) || [, ""])[1].toLowerCase();
  return !!EDITABLE_IMAGE_EXTS[ext];
}
